import BaseController from "confinus_ui/base_controller";
import { SafeHTMLContent } from "lib/types";
import { localeMessage } from "lib/i18n";
import { markAsSafeHTML, safelySetInnerHTML } from "lib/util/safe_html";
import { replaceContentsWithChildren } from "lib/util/replace_with_dom_nodes";
import axios from "lib/axios";

export default class DropdownController extends BaseController {
  public static targets = ["menu", "button"];

  public static values = {
    leaveTimeout: Number,
    contentHref: { type: String, default: null },
    placeholder: { type: String, default: null },
  };

  declare private readonly leaveTimeoutValue: number;
  declare private readonly contentHrefValue?: string;
  declare private readonly placeholderValue?: string;

  declare private readonly menuTarget: HTMLDivElement;
  declare private readonly buttonTarget: HTMLButtonElement;
  declare private readonly hasButtonTarget: boolean;

  public static classes = [
    "entering",
    "enteringFrom",
    "enteringTo",
    "leaving",
    "leavingFrom",
    "leavingTo",
  ];

  declare protected readonly enteringClasses: string[];
  declare protected readonly enteringFromClasses: string[];
  declare protected readonly enteringToClasses: string[];
  declare protected readonly leavingClasses: string[];
  declare protected readonly leavingFromClasses: string[];
  declare protected readonly leavingToClasses: string[];

  private shown: boolean = false;

  // Look at the hideOnClickOutside method (line 51-67). It checks if the button was clicked and returns early to avoid
  // hiding. However, there's a timing issue here. When the dropdown button is clicked to open the dropdown:
  //
  // 1. The toggle method is called which calls show()
  // 2. The dropdown opens and shown becomes true
  // 3. The click event then bubbles up to the window
  // 4. The hideOnClickOutside handler fires, sees that shown is now true, and immediately hides the dropdown
  // So, we need the isToggling flag, at least till we find a better fix
  private isToggling: boolean = false;

  public toggle(event: Event) {
    // Set flag to prevent immediate hiding from the same click event
    this.isToggling = true;

    if (this.shown) {
      this.hide();
    } else {
      this.show();
    }

    // Reset flag after a brief delay to allow event propagation to complete
    setTimeout(() => {
      this.isToggling = false;
    }, 0);
  }

  public hideOnClickOutside(event?: Event) {
    if (!this.shown) {
      return;
    }

    // Don't hide if we're in the middle of toggling
    if (this.isToggling) {
      return;
    }

    // If the menu itself is clicked, we don't want to hide the menu.
    if (event && this.menuTarget.contains(event.target as Node)) {
      return;
    }
    if (
      event &&
      this.hasButtonTarget &&
      this.buttonTarget.contains(event.target as Node)
    ) {
      return;
    }
    this.hide();
  }

  public hide() {
    this.displayMenu(false);
    this.shown = false;
  }

  public show() {
    this.prepareContent();
    this.displayMenu(true);
    this.shown = true;
  }

  private displayMenu(state: boolean) {
    this.toggleTargetElementTransitionClasses(
      this.menuTarget,
      state,
      this.enteringClasses,
      this.enteringFromClasses,
      this.enteringToClasses,
      this.leavingClasses,
      this.leavingFromClasses,
      this.leavingToClasses,
      this.leaveTimeoutValue,
    );
  }

  protected async prepareContent() {
    if (this.placeholderValue) {
      this.setContent(markAsSafeHTML(this.placeholderValue));
    }
    if (this.contentHrefValue) {
      this.getContent(this.contentHrefValue)
        .then((c) => {
          this.setContent(c);
        })
        .catch((err) => {
          console.error(
            "Could not fetch content for dropdown",
            this.contentHrefValue,
            err,
          );
          const errorMessage = localeMessage("generic_server_error");
          this.setContent(markAsSafeHTML(errorMessage));
        });
    }
  }

  private async getContent(contentHref: string): Promise<SafeHTMLContent> {
    return axios
      .get<string>(contentHref, {
        headers: {
          "Content-Type": "text/html",
        },
      })
      .then((response) => {
        return markAsSafeHTML(response.data);
      });
  }

  private setContent(content: SafeHTMLContent) {
    replaceContentsWithChildren(this.menuTarget, this.createContent(content));
  }

  private createContent(content: SafeHTMLContent) {
    const contentContainer = document.createElement("div");
    contentContainer.id = `${this.element.id}-content`;

    // We likely want to allow arbitrary HTML here so that the dialog content can be formatted appropriately, and allow things like other MDC components to be rendered inside.
    // The problem with allowing arbitrary HTML to be rendered is that it opens the possibility of a XSS attack.
    // To try and mitigate this issue, we ask that the developer explicitly wraps the content inside an object with a `__safe` property.
    // Of course, this can't guarantee that a developer is passing safe content, but it at least gets them thinking about the dangers of XSS when pushing content to be displayed.
    // https://www.owasp.org/index.php/Cross-site_Scripting_(XSS)
    safelySetInnerHTML(contentContainer, content);

    return contentContainer;
  }
}
